import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  createAuthorSubscriptionPrice,
  createSubscriptionPlanSelector,
} from '../../ducks/stripe.duck';
import { userSelector } from '../../ducks/user.duck';
import { getUserSubscriptionLookupkeys } from '../../util/userDataExtraction';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import css from './BrandSubscriptionPriceModal.module.css';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import FieldPhoneNumberInput from '../FieldPhoneNumberInput/FieldPhoneNumberInput';
import { emailFormatValid } from '../../util/validators';

const BrandSubscriptionPriceModal = ({ isOpen, onClose, intl, values, selectedSubscription }) => {
  const dispatch = useDispatch();
  const { createSubscriptionPriceInProgress } = useSelector(createSubscriptionPlanSelector);
  const currentUser = useSelector(userSelector);

  const selectedProduct = selectedSubscription?.product || {};

  const initialValues = {
    title: selectedProduct?.name || '',
    description: selectedSubscription?.metadata?.description || '',
    subscriptionType: selectedSubscription?.metadata?.subscriptionType || 'Recurring',
    price: selectedSubscription?.unit_amount ? selectedSubscription.unit_amount / 100 : '',
    billingCycle: selectedSubscription?.recurring?.interval || '',
    customFeatures: {},
    tasks: [''],
  };

  const required = value =>
    value
      ? undefined
      : intl.formatMessage({
          id: 'CreateSubscriptionPriceModal.required',
          defaultMessage: 'Required',
        });

  const handleCreateSubscriptionPlan = async formValues => {
    const {
      title,
      description,
      price,
      billingCycle,
      customFeatures,
      tasks,
      customerServiceEmail,
      phoneNumber,
    } = formValues || {};
    await dispatch(
      createAuthorSubscriptionPrice({
        title,
        description,
        amount: price,
        subscriptionType: 'Recurring',
        billingCycle,
        customFeatures,
        tasks,
        authorId: currentUser?.id?.uuid,
        lookupKeys: getUserSubscriptionLookupkeys(currentUser),
        customerServiceEmail,
        phoneNumber,
      })
    );

    onClose();
  };

  return (
    <div className={css.createSubscriptionPriceModal}>
      <Modal
        id="subscriptionPlanModal"
        isOpen={isOpen}
        onClose={onClose}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={intl.formatMessage({
          id: 'CreateSubscriptionPriceModal.brandTitle',
          defaultMessage: 'Create Subscription Box',
        })}
      >
        <Form
          onSubmit={handleCreateSubscriptionPlan}
          initialValues={initialValues}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, form, values, invalid }) => (
            <form onSubmit={handleSubmit}>
              <div className={css.formContent}>
                {/* Title Field */}
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.titleLabel',
                        defaultMessage: 'Title',
                      })}
                    </label>
                    <Field
                      name="title"
                      component="input"
                      type="text"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.titlePlaceholder',
                        defaultMessage: 'Subscription Name',
                      })}
                      validate={required}
                    />
                  </div>
                </div>

                {/* Description Field */}
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.descriptionLabel',
                        defaultMessage: 'Description',
                      })}
                    </label>
                    <Field
                      name="description"
                      component="textarea"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.descriptionPlaceholder',
                        defaultMessage: 'Details about the subscription',
                      })}
                      validate={required}
                    />
                  </div>
                </div>

                {/* Price and Billing Cycle Fields */}
                <div className={classNames(css.formRow)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.priceLabel',
                        defaultMessage: 'Price in USD',
                      })}
                    </label>
                    <Field
                      name="price"
                      component="input"
                      type="number"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.pricePlaceholder',
                        defaultMessage: 'Enter price',
                      })}
                      validate={required}
                    />
                  </div>

                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.billingCycleLabel',
                        defaultMessage: 'Billing Cycle',
                      })}
                    </label>
                    <Field name="billingCycle" component="select" validate={required}>
                      <option value="">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.selectCycle',
                          defaultMessage: 'Select cycle',
                        })}
                      </option>
                      <option value="month">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.monthlyOption',
                          defaultMessage: 'Monthly',
                        })}
                      </option>
                      <option value="year">
                        {intl.formatMessage({
                          id: 'CreateSubscriptionPriceModal.yearlyOption',
                          defaultMessage: 'Yearly',
                        })}
                      </option>
                    </Field>
                  </div>
                </div>

                {/* Price and Billing Cycle Fields */}
                <div className={classNames(css.formRow)}>
                  <div className={css.formFld}>
                    <FieldTextInput
                      id="customerServiceEmail"
                      name="customerServiceEmail"
                      autoComplete="email"
                      placeholder={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.emailPlaceholder',
                        defaultMessage: 'Enter email',
                      })}
                      label={intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.emailLabel',
                        defaultMessage: 'Customer Service Email',
                      })}
                      validate={emailFormatValid(
                        intl.formatMessage({
                          id: 'LoginForm.emailInvalid',
                        })
                      )}
                    />
                  </div>
                  <div className={css.formFld}>
                    <FieldPhoneNumberInput
                      className={css.phone}
                      name="phoneNumber"
                      id={'phoneNumber'}
                      label={intl.formatMessage({ id: 'ContactDetailsForm.phoneLabel' })}
                      placeholder={intl.formatMessage({
                        id: 'ContactDetailsForm.phonePlaceholder',
                      })}
                      // validate={required}
                    />
                  </div>
                </div>
                {/* Tasks Field Array */}
                <div className={classNames(css.formRow, css.fullWidth)}>
                  <div className={css.formFld}>
                    <label>
                      {intl.formatMessage({
                        id: 'CreateSubscriptionPriceModal.brandTasksLabel',
                        defaultMessage: 'Add Subscription Item',
                      })}
                    </label>
                    <FieldArray name="tasks">
                      {({ fields }) => (
                        <div className={css.tasksContainer}>
                          {fields.map((name, index) => (
                            <div key={name} className={css.taskItem}>
                              <Field
                                name={name}
                                component="input"
                                type="text"
                                placeholder={intl.formatMessage({
                                  id: 'CreateSubscriptionPriceModal.brandTaskPlaceholder',
                                  defaultMessage: 'Enter item',
                                })}
                                validate={required}
                                className={css.inputField}
                              />
                              <button
                                type="button"
                                onClick={() => fields.remove(index)}
                                className={css.removeButton}
                              >
                                {intl.formatMessage({
                                  id: 'CreateSubscriptionPriceModal.removeTask',
                                  defaultMessage: 'Remove',
                                })}
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => fields.push('')}
                            className={css.addButton}
                          >
                            {intl.formatMessage({
                              id: 'CreateSubscriptionPriceModal.addBrandItem',
                              defaultMessage: 'Add Another Item',
                            })}
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                </div>

                {/* Submit Button */}
                <div className={css.buttonWrapper}>
                  <Button
                    type="submit"
                    inProgress={createSubscriptionPriceInProgress}
                    className={css.submitButton}
                    disabled={invalid}
                  >
                    {intl.formatMessage({
                      id: 'CreateSubscriptionPriceModal.submitButton',
                      defaultMessage: 'Save',
                    })}
                  </Button>
                </div>
              </div>
            </form>
          )}
        />
      </Modal>
    </div>
  );
};

export default BrandSubscriptionPriceModal;
